import { supabase } from '@shared/lib/supabase';
import { UserGuideProgress, UserGuideProgressSummary } from '../types/userGuide.types';

export const userGuideService = {
  async getUserProgress(userId: string): Promise<Map<string, UserGuideProgress>> {
    try {
      const { data, error } = await supabase
        .from('user_guide_progress')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;

      const progressMap = new Map<string, UserGuideProgress>();
      data?.forEach((record) => {
        progressMap.set(record.article_id, {
          articleId: record.article_id,
          completed: record.completed,
          completedAt: record.completed_at ? new Date(record.completed_at) : undefined,
          bookmarked: record.bookmarked,
          lastViewedAt: new Date(record.last_viewed_at),
          timeSpentSeconds: record.time_spent_seconds,
        });
      });

      return progressMap;
    } catch (error) {
      console.error('Error fetching user guide progress:', error);
      return new Map();
    }
  },

  async markArticleCompleted(userId: string, articleId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_guide_progress')
        .upsert(
          {
            user_id: userId,
            article_id: articleId,
            completed: true,
            completed_at: new Date().toISOString(),
            last_viewed_at: new Date().toISOString(),
          },
          {
            onConflict: 'user_id,article_id',
          }
        );

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error marking article completed:', error);
      return false;
    }
  },

  async markArticleIncomplete(userId: string, articleId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_guide_progress')
        .upsert(
          {
            user_id: userId,
            article_id: articleId,
            completed: false,
            completed_at: null,
            last_viewed_at: new Date().toISOString(),
          },
          {
            onConflict: 'user_id,article_id',
          }
        );

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error marking article incomplete:', error);
      return false;
    }
  },

  async toggleBookmark(userId: string, articleId: string, bookmarked: boolean): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_guide_progress')
        .upsert(
          {
            user_id: userId,
            article_id: articleId,
            bookmarked,
            last_viewed_at: new Date().toISOString(),
          },
          {
            onConflict: 'user_id,article_id',
          }
        );

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      return false;
    }
  },

  async recordArticleView(userId: string, articleId: string, timeSpentSeconds: number = 0): Promise<void> {
    try {
      const { data: existing } = await supabase
        .from('user_guide_progress')
        .select('time_spent_seconds')
        .eq('user_id', userId)
        .eq('article_id', articleId)
        .maybeSingle();

      const totalTime = (existing?.time_spent_seconds || 0) + timeSpentSeconds;

      await supabase
        .from('user_guide_progress')
        .upsert(
          {
            user_id: userId,
            article_id: articleId,
            last_viewed_at: new Date().toISOString(),
            time_spent_seconds: totalTime,
          },
          {
            onConflict: 'user_id,article_id',
          }
        );
    } catch (error) {
      console.error('Error recording article view:', error);
    }
  },

  async getProgressSummary(userId: string): Promise<UserGuideProgressSummary | null> {
    try {
      const { data, error } = await supabase.rpc('get_user_guide_progress_summary', {
        p_user_id: userId,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        const summary = data[0];
        return {
          totalArticles: Number(summary.total_articles),
          completedArticles: Number(summary.completed_articles),
          bookmarkedArticles: Number(summary.bookmarked_articles),
          completionPercentage: Number(summary.completion_percentage),
          totalTimeSpentSeconds: Number(summary.total_time_spent_seconds),
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching progress summary:', error);
      return null;
    }
  },
};
